import { Food } from './food';
import { Ingredient } from './ingredient';

/**
 * The name of the one measure a recipe is allowed to carry. Its `amount` is how many servings
 * the whole recipe makes, so the weight of one serving is `weight_g / amount`.
 *
 * This is a canonical English value: it is what gets saved to the database and what every
 * comparison checks, so it must never change. Only the text shown to the user is translated
 * (translateMeasureName in the client's util/formatting.ts renders it as "serving").
 *
 * No other kind of food may carry it -- the food editor strips a measure with this name off
 * every non-recipe food when it saves.
 */
export const RECIPE_SERVINGS_MEASURE_NAME = 'prepared';

export interface Recipe extends Food {
  /** List of ingredients in this recipe. */
  ingredients: Ingredient[];
  /** MarkDown format of recipe instructions. */
  instructions: string;
}

/**
 * Class that allows you to instantiate an empty Recipe object
 * Uses Declaration merging with interface of the same name
 */
export class Recipe {
  constructor() {
    this.name = '';
    this.ingredients = [];
    this.instructions = '';
  }
}

/**
 * Type guard for Recipe item
 * Checks if food is a Recipe (includes legacy meal IDs for backwards compat)
 */
export function isRecipe(item: Food): item is Recipe {
  return item && (<Recipe>item).ingredients && isRecipeById(item._id);
}

/**
 * Checks if a food ID is a recipe ID (includes legacy meal IDs for backwards compat)
 */
export function isRecipeById(foodId: string): boolean {
  return foodId.startsWith('food/+UDR') || foodId.startsWith('food/$OFR') || isLegacyMealId(foodId);
}

/**
 * Checks if a food ID is a legacy meal ID (food/+UDM or meal/)
 * Legacy meals are treated as recipes but cannot be published.
 */
export function isLegacyMealId(foodId: string): boolean {
  return foodId.startsWith('food/+UDM') || foodId.startsWith('meal/');
}

/**
 * Calculate total weight from recipe ingredients.
 * This is the sum of all ingredient weights.
 *
 * It only ever reads the ingredient list, so it takes anything that HAS one --
 * a recipe, or a meal the user already logged. Both are made of ingredients and
 * both need their total weight worked out the same way.
 */
export function calculateRecipeTotalWeight(recipe: Pick<Recipe, 'ingredients'>): number {
  if (!recipe.ingredients?.length) return 0;
  return recipe.ingredients.reduce((sum, ing) => sum + ing.weight_g, 0);
}

/**
 * Ensure recipe has valid totalWeight_g (auto-fix if missing/zero).
 * Call this when loading a recipe to ensure totalWeight_g is always set correctly.
 */
export function ensureRecipeTotalWeight(recipe: Recipe): void {
  if (!recipe.totalWeight_g || recipe.totalWeight_g <= 0) {
    recipe.totalWeight_g = calculateRecipeTotalWeight(recipe);
  }
}
